import { Box, Button, Stack, Typography } from '@mui/material'
import * as monaco from 'monaco-editor'
import { useCallback, useState } from 'react'

import { RecipeTextEditor, useConfirm } from '@/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'

import IModelDecoration = monaco.editor.IModelDecoration
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useSchema } from '@/hooks'
import { safeCypressID } from '@/utils'

import DataDumpValidator from '../components/DataDumpValidator'

export type DataDumpCustomComponent = {
  dataDump: string
  name: string
}

type TextEditorState = {
  errors: IModelDecoration[]
  text: string
}

export const DataDumpEditor = () => {
  const schema = useSchema({
    name: 'DataDumpConfig',
    version: '1.0.0',
  })

  const { componentID } = useComponentProvider()
  const component = useGetCustomComponent<DataDumpCustomComponent>(componentID)
  const updateComponent =
    useUpdateCustomComponent<DataDumpCustomComponent>(componentID)

  const [dataDump, setDataDump] = useState<TextEditorState>({
    errors: [],
    text: component.data?.settings?.dataDump ?? '',
  })

  const confirm = useConfirm()
  const saveDataDump = async () => {
    try {
      console.log(dataDump.errors)
      if (dataDump.errors.length !== 0)
        await confirm({
          confirmationButtonProps: { color: 'primary', variant: 'contained' },
          content: (
            <>
              <Typography>
                {dataDump.errors.length > 1
                  ? 'There are some errors in your data dump'
                  : 'There is an error in your data dump'}
              </Typography>
              <Stack>
                {dataDump.errors.map((err) => (
                  <Typography key={err.id} variant="caption">
                    {/* @ts-ignore */}
                    {dataDump.text.slice(err?.start - 1, err?.end)}
                  </Typography>
                ))}
              </Stack>
              <Typography>Are you sure that you want to save? </Typography>
            </>
          ),
          title: 'Save Data Dump',
        })
      await updateComponent.mutateAsync({
        id: componentID,
        settings: {
          dataDump: dataDump.text,
        },
      })
    } catch (e) {
      console.log(e)
    }
  }

  const generateSchema = useCallback(
    (schema: any) => ({
      ...schema,
      properties: { data: schema.properties.data },
      required: ['data'],
    }),
    []
  )

  const onChange = useCallback(
    ({ errors, text }: { text: string; errors: IModelDecoration[] }) => {
      setDataDump({ errors, text })
    },
    []
  )
  if (schema.isLoading) return null
  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <DataDumpValidator dataDump={dataDump.text} />
      <Button
        id={safeCypressID('save_recipe_code')}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translateY(-36px)',
        }}
        variant="contained"
        onClick={saveDataDump}
      >
        Save
      </Button>
      <RecipeTextEditor
        schema={generateSchema(schema.data)}
        value={dataDump.text}
        onChange={onChange}
      />
    </Box>
  )
}
